<template>
  <div
    :class="main ? '' : 'col-xs-12 col-sm-11 col-md-9 col-lg-8 col-centered'"
    class="hr-steps"
  >
    <hr-module-header :title="t('hr-steps:title', { name: user.name })">
      <p>{{ t('hr-steps:description', { companyName: company.name }) }}</p>
    </hr-module-header>

    <div class="hr-steps__HRStepCard">
      <div class="hr-steps__section-title">{{ t('hr-steps:forms') }}</div>
      <hr-step-card
        v-for="(step, index) in categorisedDocuments.formList"
        :key="index"
        :title="step.title"
        :active="state.active === step.slug"
        :completion="stepCardCompletionMap[step.name]"
        :documentName="step.name"
        :disabled="isStepDisabled(step, index)"
        @click="open(step, index)"
      >
        <template v-slot:icon>
          <hr-co-icon :name="state.documentToIcon[countryCode][step.name]" />
        </template>
      </hr-step-card>
      <div
        v-if="categorisedDocuments.documentList.length"
        class="hr-steps__section-title"
      >
        {{ t('hr-steps:documents') }}
      </div>
      <hr-step-card
        v-for="(step, index) in categorisedDocuments.documentList"
        :key="categorisedDocuments.formList.length + index"
        :title="step.title"
        :active="state.active === step.slug"
        :completion="stepCardCompletionMap[step.name]"
        :documentName="step.name"
        :disabled="isStepDisabled(step, index)"
        @click="open(step, index)"
      >
        <template v-slot:icon>
          <hr-co-icon :name="state.documentToIcon[countryCode][step.name]" />
        </template>
      </hr-step-card>
      <co-button
        type="primary"
        size="large"
        class="__review-button"
        data-test-id="hr-review-and-submit-button"
        :disabled="isDisabledForReview"
        @click="review"
        >{{ t('hr-footer:review-and-submit') }}</co-button
      >
      <p class="__disclaimer">
        {{ t('hr-footer:disclaimer') }}
        <a
          class="__disclaimer-link"
          data-test-id="disclaimer-link"
          :href="disclaimerLink"
          target="__blank"
        >
          {{ t('hr-footer:privacy-policy') }}</a
        >

        {{ t('hr-footer:information') }}
      </p>
    </div>
  </div>
</template>

<script>
import HrStepCard from '@/components/hr-step-card';
import { reactive, computed } from 'vue';
import store from '@/store';
import router from '@/router';
import HrModuleHeader from '@/components/hr-module-header.vue';
import { CoButton } from '@deputy/copilot2';
import HrCoIcon from '@/components/hr-co-icon';
import t from '@/lib/i18next';
import svc from '@/core/svc-client';
import { HR_ONBOARDING_STATES } from '@/core/onboarding-states';
import { COUNTRY } from '@/core/country';
import { logWebHrError } from '@/core/logger/log-wrapper';
import { DOCUMENT_STATUSES } from '@/definitions';
import { shouldEnableLegalName } from '@/core/feature-flags';
import { DOCUMENT_NAMES } from '@/core/field-names';
import { COMPLETION_PERCENTAGES } from '@/modules/definitions';

export default {
  name: 'hr-steps',
  components: { HrStepCard, HrModuleHeader, HrCoIcon, CoButton },
  emits: ['step-changed', 'review-and-submit'],
  props: {
    main: {
      type: Boolean,
      default: false,
    },
    currentDocument: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const countryCode = store.state.onboarding?.countryCode || COUNTRY.AU;
    const disclaimerLink = `https://www.deputy.com/${countryCode.toLowerCase()}/terms/privacy-policy`;

    const isLegalNameEnabled = shouldEnableLegalName();

    const state = reactive({
      active: '',
      documentToIcon: {
        AU: {
          personalDetails: 'person',
          bankDetails: 'dollar',
          tfnDeclaration: 'briefcase',
          superannuation: 'star',
          additionalQuestions: 'question',
          fileUploads: 'document',
          docSignatureRequests: 'sign',
          workRights: 'briefcase',
          superAPI: 'tax',
        },
        GB: {
          personalDetails: 'person',
          bankDetails: 'pound',
          taxDetails: 'tax',
          workRights: 'briefcase',
          additionalQuestions: 'question',
          fileUploads: 'document',
          docSignatureRequests: 'sign',
        },
        US: {
          personalDetails: 'person',
          bankDetails: 'dollar',
          employmentEligibilityVerification: 'briefcase',
          federalTaxForm: 'tax',
          stateTaxForm: 'tax',
          additionalQuestions: 'question',
          fileUploads: 'document',
          docSignatureRequests: 'sign',
        },
      },
    });

    const user = computed(() => store.state.user);
    const company = computed(() => store.state.company);
    const documents = computed(() => store.getters.documents);

    const submissionRateMap = computed(() => {
      return store.getters.documents.reduce((acc, doc) => {
        const isDocumentSubmittedBefore =
          store.getters.indexedDocumentSubmissionStatuses[doc.name] ===
          DOCUMENT_STATUSES.SUBMITTED;
        // We have to do this because we need to handle the case when the user
        // updates again after submitted already.
        // So we're checking FE completion rate to remove checked mark.
        const isAllDocumentFilled =
          store.getters.indexedDocuments[doc.name].completion ===
          COMPLETION_PERCENTAGES.COMPLETE;

        acc[doc.name] =
          isDocumentSubmittedBefore && isAllDocumentFilled
            ? COMPLETION_PERCENTAGES.COMPLETE
            : COMPLETION_PERCENTAGES.INCOMPLETE;

        return acc;
      }, {});
    });

    const stepCardCompletionMap = computed(() => {
      if (countryCode === 'US' || !isLegalNameEnabled) {
        return store.getters.documents.reduce((acc, doc) => {
          acc[doc.name] = doc.completion;
          return acc;
        }, {});
      }

      return submissionRateMap.value;
    });

    const isDisabledForReview = computed(() => {
      if (!isLegalNameEnabled || countryCode === COUNTRY.US) {
        return (
          !store.getters.isDocumentsComplete ||
          store.state.documentErrors?.length > 0
        );
      }

      return !Object.values(submissionRateMap.value).every(
        (rate) => rate === COMPLETION_PERCENTAGES.COMPLETE
      );
    });

    function isDocumentListDocuments(documentName) {
      return (
        documentName === 'docSignatureRequests' ||
        documentName === 'fileUploads'
      );
    }

    const shouldShowSuperAPI = store.getters.isSuperAPIEnabled;

    const categorisedDocuments = computed(() => {
      return (documents.value || []).reduce(
        (result, doc) => {
          if (isDocumentListDocuments(doc.name)) {
            result.documentList.push(doc);
            return result;
          }

          const shouldIgnoreTnfAndSuper =
            shouldShowSuperAPI &&
            (doc.name === DOCUMENT_NAMES.TFN_DECLARATION ||
              doc.name === DOCUMENT_NAMES.SUPERANNUATION);
          if (shouldIgnoreTnfAndSuper) {
            return result;
          }

          result.formList.push(doc);
          return result;
        },
        { formList: [], documentList: [] }
      );
    });

    const disabledFormTabsMap = computed(() => {
      const disabledFormTabs = {};

      const allFormsInOrder = categorisedDocuments.value.formList.concat(
        categorisedDocuments.value.documentList
      );

      allFormsInOrder.forEach((form, index) => {
        if (form.name === 'personalDetails') {
          disabledFormTabs[form.name] = false;
          return;
        }

        const prevForm = allFormsInOrder[index - 1];
        const isPrevFormCompleted =
          submissionRateMap.value[prevForm.name] ===
          COMPLETION_PERCENTAGES.COMPLETE;
        const isPrevDisabled = disabledFormTabs[prevForm.name] === true;
        const isCurrentTabCompleted =
          submissionRateMap.value[form.name] ===
          COMPLETION_PERCENTAGES.COMPLETE;

        // Cascade: if previous tab is disabled, current must be disabled too
        // This is to prevent the forms which are filled before legal name initiative, to have empty
        // legal names filled at Super and TFN declaration steps.
        if (isPrevDisabled || !isPrevFormCompleted) {
          disabledFormTabs[form.name] = true;
          return;
        }

        if (!isPrevFormCompleted && !isCurrentTabCompleted) {
          disabledFormTabs[form.name] = true;
          return;
        }

        disabledFormTabs[form.name] = false;
      });

      return disabledFormTabs;
    });

    function goToNextStep({ nextDocument, currentStep }) {
      emit('step-changed', { nextStep: nextDocument.name, currentStep });
      store.commit('menuToggle', false);
      router.push({
        name: 'document-module',
        params: {
          document: JSON.stringify(nextDocument),
          slug: nextDocument.slug,
        },
      });
      state.active = nextDocument.slug;
    }

    function openNextStep({ nextDocument }) {
      let currentStep = documents.value.find(
        (item) => item.slug === state.active
      )?.name;
      if (!state.active) {
        currentStep = null;
      }
      goToNextStep({ nextDocument, currentStep });
    }

    function open(documentToOpen, index = 0) {
      if (isStepDisabled(documentToOpen, index)) {
        return;
      }

      openNextStep({ nextDocument: documentToOpen });
    }

    function handleNonUsStepDisabled({ currDocument }) {
      return disabledFormTabsMap.value[currDocument.name];
    }

    function isStepDisabled(currDocument, index = 0) {
      if (countryCode !== COUNTRY.US && !isLegalNameEnabled) {
        return false;
      }

      if (currDocument.name === 'personalDetails') {
        return false;
      }

      if (countryCode !== COUNTRY.US && isLegalNameEnabled) {
        return handleNonUsStepDisabled({
          currDocument,
        });
      }

      const isFromDocumentList = categorisedDocuments.value.documentList.some(
        (doc) => doc.name === currDocument.name
      );

      if (isFromDocumentList) {
        if (index === 0) {
          return (
            categorisedDocuments.value.formList[
              categorisedDocuments.value.formList.length - 1
            ].completion < COMPLETION_PERCENTAGES.COMPLETE
          );
        }
        return (
          categorisedDocuments.value.documentList[index - 1].completion <
          COMPLETION_PERCENTAGES.COMPLETE
        );
      }

      if (index) {
        return (
          categorisedDocuments.value.formList[index - 1].completion <
          COMPLETION_PERCENTAGES.COMPLETE
        );
      }
    }

    async function review() {
      try {
        // Only call save when there is a current document selected
        if (Object.keys(props.currentDocument).length) {
          emit('review-and-submit');
          return;
        }

        await svc.onboarding.status(store.state.onboarding.id, 'REVIEW');
        store.commit('setOnboardingStatus', HR_ONBOARDING_STATES.REVIEW);
        await router.push({ name: 'view-module' });
      } catch (e) {
        logWebHrError({
          message: 'Failed to set review status',
          source: 'hr-steps',
          metadata: {
            isDocumentSelected: !!Object.keys(props.currentDocument).length,
          },
          error: e,
        });
      }
    }

    state.active = router.currentRoute.value.params.slug;
    return {
      t,
      state,
      store,
      user,
      company,
      categorisedDocuments,
      open,
      countryCode,
      COUNTRY,
      disclaimerLink,
      isStepDisabled,
      review,
      isDisabledForReview,
      stepCardCompletionMap,
    };
  },
};
</script>

<style scoped lang="scss">
@import '~@/core/style-vars.scss';

.hr-steps .hr-module-header h3 {
  font-size: 24px !important;
}

.hr-steps__HRStepCard {
  padding-top: 12px;
}

@media only screen and (max-width: 768px) {
  .hr-steps__HRStepCard {
    margin-bottom: 0px;
  }
}

.hr-steps__section-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 16px;
}

.__review-button {
  height: 45px;
  padding-top: 4px;
  padding-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 20px;
}

.__disclaimer {
  color: var(--co-text-secondary);
  max-width: 500px;
  font-size: 10px;
  font-weight: normal;
  padding: 15px 0;
  text-align: left;

  @media only screen and (max-width: 768px) {
    text-align: center;
  }
}

.__disclaimer-link {
  color: var(--co-text-highlight);
}

.__disclaimer-link:hover {
  text-decoration: underline;
}
</style>
